import { useEffect } from "react";
import { useLocale } from "../lib/i18n";

interface Props {
  open: boolean;
  onClose: () => void;
  onGoogle: () => void;
  onApple: () => void;
}

export function SignInPrompt({ open, onClose, onGoogle, onApple }: Props) {
  const { t, locale } = useLocale();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const he = locale === "he";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(6, 18, 46, 0.72)" }}
      onClick={onClose}
    >
      <div
        dir={he ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-[420px] sm:w-[420px] rounded-t-3xl sm:rounded-3xl px-6 pt-6 pb-8"
        style={{
          background: "var(--navy-900)",
          border: "1px solid rgba(212, 162, 74, 0.28)",
          color: "var(--parchment-soft)",
        }}
      >
        <p
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: "var(--gold-400)",
            opacity: 0.9,
          }}
        >
          {t("common.signIn")}
        </p>
        <h2
          className="font-serif"
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "var(--parchment-soft)",
            marginTop: 4,
            lineHeight: 1.2,
          }}
        >
          {he ? "שמירת התקדמות בין מכשירים" : "Keep your progress across devices"}
        </h2>
        <p
          className="mt-2"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 13,
            lineHeight: 1.5,
            opacity: 0.75,
          }}
        >
          {he
            ? "ההתחברות היא רשות. אפשר לשחק גם בלעדיה, הסטטיסטיקה תישמר רק במכשיר הזה."
            : "Sign-in is optional. You can play without it — stats will only be stored on this device."}
        </p>

        <button
          onClick={() => {
            onApple();
            onClose();
          }}
          className="w-full mt-5 py-3 rounded-2xl flex items-center justify-center gap-2 font-semibold"
          style={{
            background: "#000",
            color: "#fff",
            fontFamily: "'Heebo', sans-serif",
            fontSize: 14,
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <AppleGlyph />
          {he ? "התחברות עם Apple" : "Sign in with Apple"}
        </button>

        <button
          onClick={() => {
            onGoogle();
            onClose();
          }}
          className="w-full mt-2 py-3 rounded-2xl flex items-center justify-center gap-2 font-semibold"
          style={{
            background: "linear-gradient(180deg, var(--gold-400), var(--gold-500))",
            color: "#2a1a00",
            fontFamily: "'Heebo', sans-serif",
            fontSize: 14,
            boxShadow: "0 4px 14px rgba(212,162,74,0.3)",
          }}
        >
          <GoogleGlyph />
          {he ? "התחברות עם Google" : "Sign in with Google"}
        </button>

        <button
          onClick={onClose}
          className="w-full mt-2 py-3 rounded-2xl transition-opacity hover:opacity-100"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 14,
            fontWeight: 500,
            color: "var(--parchment-soft)",
            background: "transparent",
            border: "1px solid rgba(250, 245, 232, 0.18)",
            opacity: 0.85,
          }}
        >
          {he ? "המשך כאורח" : "Continue as guest"}
        </button>
      </div>
    </div>
  );
}

function AppleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden fill="currentColor">
      <path d="M11.2 8.4c0-1.5 1.2-2.2 1.3-2.3-.7-1-1.8-1.2-2.2-1.2-.9-.1-1.8.6-2.3.6-.5 0-1.2-.5-2-.5-1 0-2 .6-2.6 1.6-1.1 1.9-.3 4.7.8 6.3.5.8 1.2 1.6 2 1.6.8 0 1.1-.5 2.1-.5s1.3.5 2.1.5 1.4-.8 1.9-1.5c.6-.9.9-1.8.9-1.8s-1.7-.7-1.7-2.8zM9.9 3.8c.4-.5.7-1.2.6-1.9-.6 0-1.3.4-1.7.9-.4.5-.7 1.2-.6 1.8.6.1 1.3-.3 1.7-.8z"/>
    </svg>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26a5.4 5.4 0 0 1-8.09-2.83H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.96 10.73A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.73V4.94H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.06l3-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.94l3 2.33A5.4 5.4 0 0 1 9 3.58z" />
    </svg>
  );
}
